/**
 * Notifications REST client (D-39). Talks to the user API Gateway — same base
 * and auth as services/social/api.ts.
 *
 * Every route is "me"-scoped server-side: the recipient is always the
 * authenticated caller, never a parameter. There is deliberately no userId in
 * any signature here, because there is no request shape that could read someone
 * else's inbox.
 *
 *   GET    /notifications                     inbox, newest ACTIVITY first
 *   GET    /notifications/unread-count        badge
 *   POST   /notifications/read                { notificationIds } | { all: true }
 *   GET    /notifications/preferences         globals + entity overrides
 *   PUT    /notifications/preferences         PATCH the three categories
 *   PUT    /notifications/preferences/entity  mute/unmute one trip or person
 *   DELETE /notifications/preferences/entity  back to inheriting the global
 *
 * NOT real-time: there is no WebSocket push for notifications yet (T3.3
 * partial), so callers poll. The badge endpoint is the cheap one to poll — it
 * reads a single counter row rather than the inbox.
 */

import { getTokensFromCache } from '../auth/utils/tokenCache';

const USER_API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_USER_ENDPOINT;

/** The switches a user sees in settings. Mirrors the server's category list. */
export type NotificationCategory = 'myPosts' | 'myTrips' | 'friendsActivity' | 'connections';

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  'myPosts',
  'myTrips',
  'friendsActivity',
  'connections',
];

export type NotificationType =
  | 'post.comment'
  | 'post.like'
  | 'trip.comment'
  | 'trip.like'
  | 'post.created'
  | 'trip.published'
  // `connections` — the graph itself changing (GAP-23).
  | 'user.followed'
  | 'user.follow_requested'
  | 'user.follow_accepted'
  | 'user.follow_accepted';

export interface Notification {
  notificationId: string;
  type: NotificationType;
  /** Most RECENT actor when the row is aggregated. Never the recipient. */
  actorId: string;
  /**
   * `user` is the graph events. For an AGGREGATED one ("N people followed you")
   * entityId is you — link to your own connections; for a single one ("Alice
   * accepted you") it is the actor — link to their profile.
   */
  entityType: 'post' | 'trip' | 'user';
  entityId: string;
  entityOwnerId?: string;
  preview?: string;
  /** Distinct actors collapsed into this row; >1 means a burst. */
  actorCount: number;
  aggregated: boolean;
  /** When the NEWEST event landed — this is the sort key, not createdAt. */
  lastActivityAt: string;
  createdAt: string;
  readAt?: string;
}

export interface NotificationPage {
  notifications: Notification[];
  /** Opaque; pass back as `cursor`. */
  nextCursor?: string;
}

export interface NotificationPreferences {
  myPosts: boolean;
  myTrips: boolean;
  friendsActivity: boolean;
  connections: boolean;
}

export interface EntityPreference {
  entityType: 'trip' | 'user';
  entityId: string;
  /** true = silenced even when the category is on; false = on even when off. */
  muted: boolean;
}

export interface PreferencesResponse {
  preferences: NotificationPreferences;
  entities: EntityPreference[];
}

function base(): string {
  if (!USER_API_ENDPOINT) {
    throw new Error('User API endpoint not configured (REACT_APP_APIGATEWAY_USER_ENDPOINT)');
  }
  return USER_API_ENDPOINT;
}

function authHeaders(): Record<string, string> {
  const token = getTokensFromCache()?.accessToken;
  if (!token) throw new Error('User not authenticated');
  return { 'Content-Type': 'application/json', Authorization: token };
}

async function request(path: string, init?: RequestInit): Promise<any> {
  const response = await fetch(`${base()}${path}`, { ...init, headers: authHeaders() });
  if (!response.ok) {
    const error: any = new Error(`Notifications request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : {};
}

export async function getNotifications(
  options: { limit?: number; cursor?: string } = {},
): Promise<NotificationPage> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  const query = params.toString();
  const data = await request(`/notifications${query ? `?${query}` : ''}`);
  return { notifications: data.notifications || [], nextCursor: data.nextCursor };
}

/**
 * Unread badge. `recount` rebuilds the counter from the rows — the stored
 * counter is a denormalization and can drift, so this is the heal path; don't
 * use it for routine polling.
 */
export async function getUnreadCount(recount = false): Promise<number> {
  const data = await request(`/notifications/unread-count${recount ? '?recount=1' : ''}`);
  return Number(data.unread || 0);
}

export async function markRead(notificationIds: string[]): Promise<void> {
  if (notificationIds.length === 0) return;
  await request('/notifications/read', {
    method: 'POST',
    body: JSON.stringify({ notificationIds }),
  });
}

export async function markAllRead(): Promise<void> {
  await request('/notifications/read', {
    method: 'POST',
    body: JSON.stringify({ all: true }),
  });
}

export async function getPreferences(): Promise<PreferencesResponse> {
  const data = await request('/notifications/preferences');
  return {
    // Absent means the user never opened settings — everything on (server default).
    preferences: data.preferences
      || { myPosts: true, myTrips: true, friendsActivity: true, connections: true },
    entities: data.entities || [],
  };
}

/** PATCH semantics: send only the categories that changed. */
export async function setPreferences(
  updates: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const data = await request('/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return data.preferences;
}

/** Mute or explicitly un-mute one trip or one person. */
export async function setEntityPreference(
  entityType: 'trip' | 'user',
  entityId: string,
  muted: boolean,
): Promise<EntityPreference> {
  const data = await request('/notifications/preferences/entity', {
    method: 'PUT',
    body: JSON.stringify({ entityType, entityId, muted }),
  });
  return data.entity;
}

/**
 * Drop the override so the entity inherits its global category again.
 * Distinct from `setEntityPreference(..., false)`, which PINS it to on.
 */
export async function clearEntityPreference(
  entityType: 'trip' | 'user',
  entityId: string,
): Promise<void> {
  const params = new URLSearchParams({ entityType, entityId });
  await request(`/notifications/preferences/entity?${params.toString()}`, { method: 'DELETE' });
}
